import type { QuestionnaireResponse } from "@medplum/fhirtypes";

import {
  LABOR_VITALS_BLOOD_PRESSURE_LINK_ID,
  LABOR_VITALS_DATE_LINK_ID,
  LABOR_VITALS_DATE_TIME_LINK_ID,
  LABOR_VITALS_DIASTOLIC_LINK_ID,
  LABOR_VITALS_METHOD_LINK_ID,
  LABOR_VITALS_NOTES_LINK_ID,
  LABOR_VITALS_PULSE_LINK_ID,
  LABOR_VITALS_ROOT_LINK_ID,
  LABOR_VITALS_SIGNATURE_INITIALS_LINK_ID,
  LABOR_VITALS_SYSTOLIC_LINK_ID,
  LABOR_VITALS_TEMPERATURE_LINK_ID,
  LABOR_VITALS_TEMPERATURE_UNIT_LINK_ID,
  LABOR_VITALS_TEMPERATURE_VALUE_LINK_ID,
  LABOR_VITALS_TIME_LINK_ID,
} from "@/encounters/components/EncounterLabor/flowsheet-forms/labor-vitals/constants/labor-vitals-link-ids";
import type { LaborVitalsFlowsheetFormValues } from "@/encounters/components/EncounterLabor/flowsheet-forms/labor-vitals/types/labor-vitals-form";
import { getEmptyLaborVitalsFlowsheetFormValues } from "@/encounters/components/EncounterLabor/flowsheet-forms/labor-vitals/utils/labor-vitals-form-defaults";
import {
  parseCodingFromNativeSelect,
  readCodingAnswerAsNativeSelectValue,
} from "@/encounters/components/EncounterLabor/flowsheet-forms/shared/labor-flowsheet-coding-select";
import {
  findQuestionnaireResponseItem,
  parseQuestionnaireResponseLocalValueDate,
  readQuestionnaireDecimalAnswer,
  readQuestionnaireSignatureInitials,
  readQuestionnaireStringAnswer,
} from "@/encounters/components/EncounterLabor/flowsheet-forms/shared/questionnaire-response-readers";
import { resolveLaborVitalsRootGroup } from "@/encounters/components/EncounterLabor/utils/resolve-labor-vitals-root-group";

export function mapQuestionnaireResponseToLaborVitalsFormValues(
  questionnaireResponse: QuestionnaireResponse | undefined,
): LaborVitalsFlowsheetFormValues {
  // Hydration uses an empty shape so a missing/malformed root never prefills "now".
  const emptyValues = getEmptyLaborVitalsFlowsheetFormValues();
  const root = resolveLaborVitalsRootGroup(questionnaireResponse?.item);
  if (!root?.item) {
    return emptyValues;
  }

  const dateTimeGroup = findQuestionnaireResponseItem(root.item, LABOR_VITALS_DATE_TIME_LINK_ID);
  const dateAnswer = findQuestionnaireResponseItem(dateTimeGroup?.item, LABOR_VITALS_DATE_LINK_ID)
    ?.answer?.[0]?.valueDate;
  const timeAnswer = findQuestionnaireResponseItem(dateTimeGroup?.item, LABOR_VITALS_TIME_LINK_ID)
    ?.answer?.[0]?.valueTime;

  const bloodPressureGroup = findQuestionnaireResponseItem(
    root.item,
    LABOR_VITALS_BLOOD_PRESSURE_LINK_ID,
  );
  const temperatureGroup = findQuestionnaireResponseItem(
    root.item,
    LABOR_VITALS_TEMPERATURE_LINK_ID,
  );

  return {
    ...emptyValues,
    vitalDate: parseQuestionnaireResponseLocalValueDate(dateAnswer),
    vitalTime: timeAnswer ?? null,
    systolic: readQuestionnaireDecimalAnswer(
      findQuestionnaireResponseItem(bloodPressureGroup?.item, LABOR_VITALS_SYSTOLIC_LINK_ID),
    ),
    diastolic: readQuestionnaireDecimalAnswer(
      findQuestionnaireResponseItem(bloodPressureGroup?.item, LABOR_VITALS_DIASTOLIC_LINK_ID),
    ),
    pulse: readQuestionnaireDecimalAnswer(
      findQuestionnaireResponseItem(root.item, LABOR_VITALS_PULSE_LINK_ID),
    ),
    temperatureValue: readQuestionnaireDecimalAnswer(
      findQuestionnaireResponseItem(temperatureGroup?.item, LABOR_VITALS_TEMPERATURE_VALUE_LINK_ID),
    ),
    temperatureUnit: readCodingAnswerAsNativeSelectValue(
      findQuestionnaireResponseItem(temperatureGroup?.item, LABOR_VITALS_TEMPERATURE_UNIT_LINK_ID),
    ),
    temperatureMethod: readCodingAnswerAsNativeSelectValue(
      findQuestionnaireResponseItem(root.item, LABOR_VITALS_METHOD_LINK_ID),
    ),
    notes: readQuestionnaireStringAnswer(
      findQuestionnaireResponseItem(root.item, LABOR_VITALS_NOTES_LINK_ID),
    ),
    signatureInitials: readQuestionnaireSignatureInitials(
      findQuestionnaireResponseItem(root.item, LABOR_VITALS_SIGNATURE_INITIALS_LINK_ID),
    ),
  };
}

/** Shape expected by `getQuestionnaireResponseFromFormValues` / labor bots (legacy intake nesting). */
export function mapLaborVitalsFormValuesToQuestionnaireValues(
  formValues: LaborVitalsFlowsheetFormValues,
): Record<string, unknown> {
  return {
    [LABOR_VITALS_ROOT_LINK_ID]: {
      [LABOR_VITALS_DATE_TIME_LINK_ID]: {
        [LABOR_VITALS_DATE_LINK_ID]: formValues.vitalDate,
        [LABOR_VITALS_TIME_LINK_ID]: formValues.vitalTime,
      },
      [LABOR_VITALS_BLOOD_PRESSURE_LINK_ID]: {
        [LABOR_VITALS_SYSTOLIC_LINK_ID]: formValues.systolic,
        [LABOR_VITALS_DIASTOLIC_LINK_ID]: formValues.diastolic,
      },
      [LABOR_VITALS_PULSE_LINK_ID]: formValues.pulse,
      [LABOR_VITALS_TEMPERATURE_LINK_ID]: {
        [LABOR_VITALS_TEMPERATURE_VALUE_LINK_ID]: formValues.temperatureValue,
        [LABOR_VITALS_TEMPERATURE_UNIT_LINK_ID]: parseCodingFromNativeSelect(
          formValues.temperatureUnit,
        ),
      },
      [LABOR_VITALS_METHOD_LINK_ID]: parseCodingFromNativeSelect(formValues.temperatureMethod),
      [LABOR_VITALS_NOTES_LINK_ID]: formValues.notes,
      [LABOR_VITALS_SIGNATURE_INITIALS_LINK_ID]: formValues.signatureInitials,
    },
  };
}
